import React, { useState, useMemo } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  Theme,
  TableSortLabel,
  Box,
} from "@mui/material";
import { observer } from "mobx-react";
import AppTableHeaderCell from "../../common/pages/AppTableHeaderCell";
import AppTablePagenation from "../../common/pages/AppTablePagenation";
import { SortType } from "../../../../models/SortType";
import { formatDate } from "../../common/functions/formatDate";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { VendorsPOReportData } from "../../../../models/Vendors/VendorsPOReportData";
import dayjs from "dayjs";
import AppTableRowCell from "../pages/AppTableRowCell";

interface VendorsPOReportTableProps {
  maxHeight?: number;
  data: VendorsPOReportData[];
}

const VendorsPOReportTable: React.FC<VendorsPOReportTableProps> = ({
  maxHeight = 600,
  data,
}) => {
  const theme = useTheme<Theme>();

  // Local state for pagination and sorting
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<string>("order_id");
  const [sortType, setSortType] = useState<SortType>(SortType.DESC);
  const recordsPerPage = 50;

  const handleRowsChange = (value: number) => {
    setCurrentPage(1);
  };

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => {
    setCurrentPage(page);
  };

  const handleSortChange = (column: string, sortType: SortType) => {
    setSortBy(column);
    setSortType(sortType);
    setCurrentPage(1); // Reset to first page when sorting
  };

  const renderSortIcon = (property: string) => {
    if (sortBy === property) {
      return sortType === SortType.ASC ? "↑" : "↓";
    }
    return "↑↓";
  };

  // Local sorting and pagination logic
  const sortedAndPaginatedData = useMemo(() => {
    // Sort the data
    const sortedData = [...data].sort((a, b) => {
      const aValue = (a as any)[sortBy];
      const bValue = (b as any)[sortBy];

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Handle dates
      if (aValue instanceof Date && bValue instanceof Date) {
        return sortType === SortType.ASC
          ? aValue.getTime() - bValue.getTime()
          : bValue.getTime() - aValue.getTime();
      }

      // Handle numbers
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortType === SortType.ASC ? aValue - bValue : bValue - aValue;
      }

      // Handle strings
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      return sortType === SortType.ASC
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });

    // Paginate the sorted data
    const startIndex = (currentPage - 1) * recordsPerPage;
    const endIndex = startIndex + recordsPerPage;
    return sortedData.slice(startIndex, endIndex);
  }, [data, sortBy, sortType, currentPage, recordsPerPage]);

  // const totalPages = Math.ceil(data.length / recordsPerPage);
  const totalCount = data.length;

  const handleDownload = () => {
    // Define headers for CSV
    const headers = [
      "S.No",
      "Inward Type",
      "PO ID",
      "PR ID",
      "Customer Name",
      "Order ID",
      "Product ID",
      "Product Name",
      "PR Product Description",
      "Purchase Quantity",
      "Purchase Rate",
      "Selling Rate",
      "Order Discount",
      "PR Created Date",
      "PO Created Date",
      "Total Received Quantity",
      "Last Received Date",
    ];

    // Convert data to CSV rows
    const rows = data.map((row, index) => [
      index + 1,
      row.inward_type,
      row.po_id,
      row.pr_id,
      row.customer_name,
      row.order_id,
      row.product_id,
      row.product_name,
      row.description,
      row.quantity,
      row.rate,
      row.selling_rate,
      row.order_discount,
      row.pr_created_date ? formatDate(row.pr_created_date) : "",
      row.po_created_date ? formatDate(row.po_created_date) : "",
      row.total_received_quantity,
      row.last_received_date ? formatDate(row.last_received_date) : "",
    ]);

    // Escape CSV special characters
    const escapeCsv = (value: any) => {
      const cell = String(value ?? "").replace(/"/g, '""');
      return `"${cell}"`;
    };

    const csvContent = [
      headers.map(escapeCsv).join(","),
      ...rows.map((row) => row.map(escapeCsv).join(",")),
    ].join("\n");

    const now = dayjs().format("YYYY-MM-DD_HH:mm:ss");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Ops_Sync_Analytics_${now}.csv`);
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Paper
      sx={{
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          padding: "16px 20px",
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.customColors.lightGreyColor,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: theme.palette.customColors.blackBorder,
            fontSize: "1.1rem",
          }}
        >
          Report
        </Typography>

        <Box display="flex" justifyContent="flex-end">
          <button
            onClick={handleDownload}
            style={{
              padding: "10px 16px",
              backgroundColor: theme.palette.customColors.purple,
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontWeight: 600,
              fontSize: "0.875rem",
              boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor =
                theme.palette.customColors.purple;
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow =
                "0 4px 12px rgba(0, 0, 0, 0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor =
                theme.palette.customColors.purple;
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 2px 6px rgba(0, 0, 0, 0.1)";
            }}
          >
            <FontAwesomeIcon icon={faDownload} fontSize="0.875rem" />
            Download Data
          </button>
        </Box>
      </Box>

      {data.length > 0 ? (
        <Box>
          <TableContainer sx={{ maxHeight: maxHeight }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      position: "sticky",
                      left: 0,
                      zIndex: 20,
                      boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                      minWidth: "60px",
                    }}
                  >
                    S.No.
                  </AppTableHeaderCell>

                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      position: "sticky",
                      left: 80,
                      zIndex: 20,
                      boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                    }}
                  >
                    <TableSortLabel
                      active={sortBy === "po_id"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        handleSortChange(
                          "po_id",
                          sortBy === "po_id" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("po_id")}</span>
                      )}
                      sx={{ fontSize: "14px" }}
                    >
                      PO ID
                    </TableSortLabel>
                  </AppTableHeaderCell>

                  <AppTableHeaderCell>
                    <TableSortLabel
                      active={sortBy === "pr_id"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        handleSortChange(
                          "pr_id",
                          sortBy === "pr_id" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("pr_id")}</span>
                      )}
                      sx={{ fontSize: "14px" }}
                    >
                      PR ID
                    </TableSortLabel>
                  </AppTableHeaderCell>

                  <AppTableHeaderCell>Inward Type</AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="250px">
                    Customer Name
                  </AppTableHeaderCell>

                  <AppTableHeaderCell>
                    <TableSortLabel
                      active={sortBy === "order_id"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        handleSortChange(
                          "order_id",
                          sortBy === "order_id" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("order_id")}</span>
                      )}
                      sx={{ fontSize: "14px" }}
                    >
                      Order ID
                    </TableSortLabel>
                  </AppTableHeaderCell>

                  <AppTableHeaderCell>
                    <TableSortLabel
                      active={sortBy === "product_id"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        handleSortChange(
                          "product_id",
                          sortBy === "product_id" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("product_id")}</span>
                      )}
                      sx={{ fontSize: "14px" }}
                    >
                      Product ID
                    </TableSortLabel>
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="250px">
                    Product Name
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    PR Product Description
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    Purchase Quantity
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    Purchase Rate
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    Selling Rate
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    Order Discount
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    <TableSortLabel
                      active={sortBy === "pr_created_date"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        handleSortChange(
                          "pr_created_date",
                          sortBy === "pr_created_date" &&
                            sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("pr_created_date")}</span>
                      )}
                      sx={{ fontSize: "14px" }}
                    >
                      PR Created Date
                    </TableSortLabel>
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    <TableSortLabel
                      active={sortBy === "po_created_date"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        handleSortChange(
                          "po_created_date",
                          sortBy === "po_created_date" &&
                            sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("po_created_date")}</span>
                      )}
                      sx={{ fontSize: "14px" }}
                    >
                      PO Created Date
                    </TableSortLabel>
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    Total Received Quantity
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    <TableSortLabel
                      active={sortBy === "last_received_date"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        handleSortChange(
                          "last_received_date",
                          sortBy === "last_received_date" &&
                            sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("last_received_date")}</span>
                      )}
                      sx={{ fontSize: "14px" }}
                    >
                      Last Received Date
                    </TableSortLabel>
                  </AppTableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedAndPaginatedData.map((row, index) => (
                  <TableRow
                    key={`${row.po_id}-${row.pr_id}-${row.order_id}-${row.product_id}-${index}`}
                    sx={{
                      backgroundColor:
                        row.inward_type === "SWAGEAZY INVENTORY"
                          ? theme.palette.action.hover
                          : theme.palette.background.paper,
                    }}
                  >
                    <AppTableRowCell>{index + 1}</AppTableRowCell>
                    <AppTableRowCell>{row.po_id}</AppTableRowCell>
                    <AppTableRowCell>{row.pr_id}</AppTableRowCell>
                    <AppTableRowCell>{row.inward_type}</AppTableRowCell>
                    <AppTableRowCell>{row.customer_name}</AppTableRowCell>
                    <AppTableRowCell>{row.order_id}</AppTableRowCell>
                    <AppTableRowCell>{row.product_id}</AppTableRowCell>
                    <AppTableRowCell>{row.product_name}</AppTableRowCell>
                    <AppTableRowCell>{row.description}</AppTableRowCell>
                    <AppTableRowCell>{row.quantity}</AppTableRowCell>
                    <AppTableRowCell>{row.rate}</AppTableRowCell>
                    <AppTableRowCell>{row.order_discount}</AppTableRowCell>
                    <AppTableRowCell>{row.selling_rate}</AppTableRowCell>
                    <AppTableRowCell>
                      {row.pr_created_date
                        ? formatDate(row.pr_created_date)
                        : ""}
                    </AppTableRowCell>
                    <AppTableRowCell>
                      {row.po_created_date
                        ? formatDate(row.po_created_date)
                        : ""}
                    </AppTableRowCell>
                    <AppTableRowCell>
                      {row.total_received_quantity}
                    </AppTableRowCell>
                    <AppTableRowCell>
                      {row.last_received_date
                        ? formatDate(row.last_received_date)
                        : ""}
                    </AppTableRowCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <AppTablePagenation
            fontSize="12px"
            pageNo={currentPage}
            limit={recordsPerPage}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            handleRowsChange={handleRowsChange}
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "200px",
            color: theme.palette.text.secondary,
          }}
        >
          <Typography variant="body1">No data available</Typography>
        </Box>
      )}
    </Paper>
  );
};

export default observer(VendorsPOReportTable);
